import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { doHttpRequestRaw } from 'libs/end-to-end/src/http/do-http-request';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { OrganizationLogoUploadUrl } from 'libs/end-to-end/src/types/organizations/organization';
import { UploadOrganizationLogoRequestSchema } from 'libs/end-to-end/src/types/organizations/upload-organization-logo-request';
import { Organization } from 'libs/end-to-end/src/types/organizations/organization';
import { LogoFileMimeType } from 'libs/end-to-end/src/logos';

/**
 * Parameters for uploading an organization logo.
 * @interface UploadLogoImageParams
 */
export interface UploadLogoImageParams {
  organization: Organization;
  logoFile: Blob;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook return type for organization logo upload functionality
 * @interface OrganizationLogoUploadHook
 */
export interface OrganizationLogoUploadHook {
  /**
   * Uploads a logo image for an organization
   * @param organization - The organization to upload the logo for
   * @param onSuccess - Optional callback function called when upload succeeds
   * @param onError - Optional callback function called when upload fails
   */
  uploadLogoImage: (
    organization: Organization,
    logoFile: Blob,
    onSuccess?: () => void,
    onError?: (error: string) => void,
  ) => void;
}

/**
 * Custom hook for handling organization logo uploads
 * On success, the organization's logoFile and logoMimeType will be updated, and the onSuccess callback will be executed if supplied. Same for onError.
 * TODO: better handling of the success and error callbacks when the is no image to upload
 * @returns {OrganizationLogoUploadHook} Hook methods for logo upload functionality
 */
export const useOrganizationLogoUpload = (): OrganizationLogoUploadHook => {
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  // The organization name and image blob to upload, and callbacks to execute on success or error
  const [uploadParams, setUploadParams] =
    useState<UploadLogoImageParams | null>(null);

  // Write pattern to get the presigned URL for the logo upload
  const getPresignedUrlPattern = useSimpleWritePattern({
    getUrlFn: () =>
      uploadParams?.organization
        ? `${getApiBaseUrl()}/organizations/${uploadParams.organization.id}/logo-upload`
        : '',
    writeSchema: UploadOrganizationLogoRequestSchema,
    method: 'POST',
    canWrite: !!uploadParams?.organization,
    responseSchema: OrganizationLogoUploadUrl,
  });

  /**
   * Initiates the logo upload process. This is the main function that should be used to upload a logo from other components.
   * It sets the organization and the callbacks to execute on success or error.
   * When the organization is set, the writer to get the presigned URL is triggered.
   * @param organization - The organization to upload the logo for
   * @param onSuccess - Optional callback function called when upload succeeds
   * @param onError - Optional callback function called when upload fails
   */
  const uploadLogoImage = useCallback(
    (
      organization: Organization,
      logoFile?: Blob,
      onSuccess?: () => void,
      onError?: (error: string) => void,
    ) => {
      if (!logoFile) {
        onSuccess?.();
        return;
      }
      setUploadParams({ organization, logoFile, onSuccess, onError });
    },
    [],
  );

  /**
   * Performs the actual upload to the presigned URL. It is executed after the presigned URL is retrieved.
   * @param response - The response containing the presigned URL
   */
  const performUpload = useCallback(
    async (response: OrganizationLogoUploadUrl) => {
      // No image selected - nothing to upload
      if (!uploadParams?.logoFile) {
        uploadParams?.onSuccess?.();
      } else {
        try {
          const logoFileBlobText = await uploadParams.logoFile.arrayBuffer();
          const result = await doHttpRequestRaw({
            url: response.url,
            logger: uiAppLogger,
            method: 'PUT',
            headers: {
              'Content-Type': uploadParams.logoFile.type || 'image/jpeg',
            },
            data: logoFileBlobText,
          });

          if (!result || result.resultType === 'err') {
            uploadParams.onError?.('Failed to upload logo');
            return;
          }

          setPresignedUrl(null);
          uploadParams.onSuccess?.();
        } catch (error) {
          const errorMessage = (error as Error).message;
          uploadParams.onError?.(errorMessage);
        } finally {
          setUploadInProgress(false);
          setUploadParams(null);
        }
      }
    },
    [uploadParams],
  );

  /**
   * Effect to handle the upload process. As soon as the uploadParams are set, the writer to get the presigned URL is triggered.
   * After the presigned URL is retrieved, the upload is performed.
   */
  useEffect(() => {
    if (
      uploadParams?.organization &&
      !uploadInProgress &&
      getPresignedUrlPattern.status === 'done' &&
      (!getPresignedUrlPattern.saved || !presignedUrl)
    ) {
      const validatedMimeType = LogoFileMimeType.safeParse(
        uploadParams.organization.logoMimeType,
      );
      if (!validatedMimeType.success) {
        return;
      }

      setUploadInProgress(true);
      getPresignedUrlPattern.saveEntity(
        {
          organizationId: uploadParams.organization.id ?? '',
          fileMimeType: validatedMimeType.data,
          fileUrl: uploadParams.organization.logoUrl ?? '',
        },
        (response) => {
          performUpload(response);
        },
      );
    }
  }, [
    getPresignedUrlPattern,
    uploadParams,
    presignedUrl,
    performUpload,
    uploadInProgress,
  ]);

  /**
   * Memoized hook return value. It only exposes the uploadLogoImage function to other components.
   * @returns {OrganizationLogoUploadHook} Hook methods for logo upload functionality
   */
  return useMemo(() => {
    return {
      uploadLogoImage,
    };
  }, [uploadLogoImage]);
};
